import type { GuestExperience } from '../lib/experience/guestExperience';
import { msg } from '@tov-plus/ui';

export type GuestWorkflowStatus =
  | 'complete'
  | 'in_progress'
  | 'not_started'
  | 'blocked';

export interface GuestWorkflowAction {
  label: string;
  href: string;
}

export interface GuestWorkflowSection {
  id: 'invite' | 'rsvp' | 'occasion' | 'gallery' | 'post_event';
  title: string;
  summary: string;
  status: GuestWorkflowStatus;
  metrics: Array<{
    label: string;
    value: string;
  }>;
  primaryAction: GuestWorkflowAction;
  secondaryActions: GuestWorkflowAction[];
}

function summarizeStatus(values: GuestWorkflowStatus[]): GuestWorkflowStatus {
  if (values.every((value) => value === 'complete')) {
    return 'complete';
  }

  if (values.includes('in_progress') || values.includes('complete')) {
    return 'in_progress';
  }

  if (values.includes('not_started')) {
    return 'not_started';
  }

  return 'blocked';
}

export function buildGuestWorkflowSections(
  experience: GuestExperience,
): GuestWorkflowSection[] {
  const summary = experience.journeySummary;
  const availableTargetSet = new Set(summary.availableTargets);
  const scheduleEnabled = availableTargetSet.has('schedule');
  const seatingEnabled = availableTargetSet.has('seating');
  const galleryEnabled = availableTargetSet.has('gallery');
  const sharedPlanningEnabled = availableTargetSet.has('shared_planning');
  const galleryOpen =
    galleryEnabled &&
    (summary.galleryItemCount > 0 || summary.galleryUploadAllowed);
  const postEventActive = summary.phase === 'post_event';

  const inviteStatus: GuestWorkflowStatus =
    summary.visibleEventCount > 0 || summary.visibleFormCount > 0
      ? 'complete'
      : 'in_progress';
  const rsvpStatus: GuestWorkflowStatus =
    summary.invitedEventCount === 0
      ? 'blocked'
      : summary.pendingRsvpEventCount === 0 && summary.pendingFormCount === 0
        ? 'complete'
        : summary.pendingRsvpEventCount < summary.invitedEventCount
          ? 'in_progress'
          : 'not_started';
  const occasionStatus = summarizeStatus([
    summary.visibleEventCount > 0 ? 'complete' : 'not_started',
    scheduleEnabled && summary.visibleScheduleCount > 0
      ? 'complete'
      : 'in_progress',
    seatingEnabled && summary.visibleSeatingCount > 0
      ? 'complete'
      : 'in_progress',
  ]);
  const galleryStatus: GuestWorkflowStatus = galleryOpen
    ? 'complete'
    : 'in_progress';
  const postEventStatus: GuestWorkflowStatus = !postEventActive
    ? 'blocked'
    : galleryOpen || summary.visibleFormCount > 0
      ? 'complete'
      : 'in_progress';

  const sections: GuestWorkflowSection[] = [
    {
      id: 'invite',
      title: msg('app.guest_workflows.invite_access'),
      summary: msg(
        'app.guest_workflows.enter_from_a_trusted_link_keep_the_same_occasion_context_on_return',
      ),
      status: inviteStatus,
      metrics: [
        {
          label: msg('app.guest_workflows.access_mode'),
          value:
            experience.accessMode === 'guest_token'
              ? msg('app.guest_workflows.invite_link')
              : msg('app.guest_workflows.claimed_access'),
        },
        {
          label: msg('general.fields.locale.label'),
          value: experience.localizationContext.resolvedLocaleCode,
        },
        {
          label: msg('app.guest_workflows.visible_events'),
          value: String(summary.visibleEventCount),
        },
      ],
      primaryAction: {
        label: msg('app.guest_workflows.review_your_access'),
        href: '/access',
      },
      secondaryActions: [
        {
          label: msg('app.guest_workflows.guest_overview'),
          href: '/',
        },
        {
          label: msg('app.guest_workflows.event_summary'),
          href: '/events',
        },
      ],
    },
    {
      id: 'rsvp',
      title: msg('app.guest_workflows.rsvp_and_forms'),
      summary: msg(
        'app.guest_workflows.handle_rsvp_and_guest_questions_from_one_calm_flow_so_attendance_and',
      ),
      status: rsvpStatus,
      metrics: [
        {
          label: msg('app.guest_workflows.invited_events'),
          value: String(summary.invitedEventCount),
        },
        {
          label: msg('app.guest_workflows.rsvp_pending'),
          value: String(summary.pendingRsvpEventCount),
        },
        {
          label: msg('app.guest_workflows.forms_pending'),
          value: String(summary.pendingFormCount),
        },
      ],
      primaryAction: {
        label:
          summary.invitedEventCount === 0
            ? msg('app.guest_workflows.review_event_summary')
            : summary.pendingRsvpEventCount > 0
              ? msg('app.guest_workflows.finish_rsvp')
              : summary.pendingFormCount > 0
                ? msg('app.guest_workflows.complete_forms')
                : msg('app.guest_workflows.review_rsvp'),
        href:
          summary.invitedEventCount === 0
            ? '/events'
            : summary.pendingRsvpEventCount > 0
              ? '/rsvp'
              : summary.pendingFormCount > 0
                ? '/forms'
                : '/rsvp',
      },
      secondaryActions: [
        {
          label: msg('general.modules.forms.name'),
          href: '/forms',
        },
        {
          label: msg('general.modules.events.name'),
          href: '/events',
        },
      ],
    },
    {
      id: 'occasion',
      title: msg('app.guest_workflows.occasion_day_flow'),
      summary: msg(
        'app.guest_workflows.browse_visible_events_check_the_guest_safe_schedule_and_see_seating_only',
      ),
      status: occasionStatus,
      metrics: [
        {
          label: msg('app.guest_workflows.event_cards'),
          value: String(summary.visibleEventCount),
        },
        {
          label: msg('app.guest_workflows.schedule_items'),
          value: scheduleEnabled ? String(summary.visibleScheduleCount) : '—',
        },
        {
          label: msg('app.guest_workflows.seating_entries'),
          value: seatingEnabled ? String(summary.visibleSeatingCount) : '—',
        },
      ],
      primaryAction: {
        label:
          scheduleEnabled && summary.visibleScheduleCount > 0
            ? `${msg('general.actions.open')} ${msg('general.modules.schedule.name')}`
            : msg('app.guest_workflows.view_events'),
        href:
          scheduleEnabled && summary.visibleScheduleCount > 0
            ? '/schedule'
            : '/events',
      },
      secondaryActions: [
        ...(seatingEnabled
          ? [
              {
                label: msg('general.modules.seating.name'),
                href: '/seating',
              },
            ]
          : []),
        {
          label: msg('general.modules.rsvp.name'),
          href: '/rsvp',
        },
      ],
    },
    ...(galleryEnabled || sharedPlanningEnabled
      ? ([
          {
            id: 'gallery',
            title: msg('app.guest_workflows.gallery_and_sharing'),
            summary: msg(
              'app.guest_workflows.return_to_approved_media_and_upload_your_own_content_only_where_the',
            ),
            status: galleryStatus,
            metrics: [
              {
                label: msg('app.guest_workflows.approved_media'),
                value: galleryEnabled ? String(summary.galleryItemCount) : '—',
              },
              {
                label: msg('app.guest_workflows.uploads'),
                value:
                  galleryEnabled && summary.galleryUploadAllowed
                    ? msg('general.actions.open')
                    : msg('general.status.closed'),
              },
              {
                label: msg('general.modules.task_boards.name'),
                value: sharedPlanningEnabled
                  ? String(summary.sharedPlanningItemCount)
                  : '—',
              },
            ],
            primaryAction: {
              label: galleryEnabled
                ? `${msg('general.actions.open')} ${msg('general.modules.gallery.name')}`
                : msg('app.guest_workflows.occasion_overview'),
              href: galleryEnabled ? '/gallery' : '/',
            },
            secondaryActions: [
              {
                label: msg('app.guest_workflows.occasion_overview'),
                href: '/',
              },
              {
                label: msg('app.guest_workflows.access_info'),
                href: '/access',
              },
            ],
          } satisfies GuestWorkflowSection,
        ] as GuestWorkflowSection[])
      : []),
    {
      id: 'post_event',
      title: msg('app.guest_workflows.post_event_return'),
      summary: msg(
        'app.guest_workflows.keep_return_visits_anchored_on_the_same_occasion_so_guests_can_still',
      ),
      status: postEventStatus,
      metrics: [
        {
          label: msg('app.guest_workflows.event_state'),
          value: postEventActive
            ? msg('app.guest_workflows.post_event')
            : msg('app.guest_workflows.upcoming_live'),
        },
        {
          label: msg('app.guest_workflows.gallery_items'),
          value: galleryEnabled ? String(summary.galleryItemCount) : '—',
        },
        {
          label: msg('app.guest_workflows.return_target'),
          value: summary.returnTarget.replace('_', ' '),
        },
      ],
      primaryAction: {
        label:
          galleryEnabled && summary.returnTarget === 'gallery'
            ? msg('app.guest_workflows.return_to_gallery')
            : `${msg('general.actions.open')} ${msg('general.modules.overview.name')}`,
        href:
          galleryEnabled && summary.returnTarget === 'gallery'
            ? '/gallery'
            : summary.returnTarget === 'schedule'
              ? '/schedule'
              : '/',
      },
      secondaryActions: [
        ...(scheduleEnabled
          ? [
              {
                label: msg('general.modules.schedule.name'),
                href: '/schedule',
              },
            ]
          : []),
        {
          label: msg('app.guest_workflows.access_details'),
          href: '/access',
        },
      ],
    },
  ];

  return sections;
}

export function getRecommendedGuestAction(sections: GuestWorkflowSection[]) {
  const actionable = sections.find((section) => section.status !== 'complete');

  return actionable?.primaryAction ?? sections[0]?.primaryAction ?? null;
}
